import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import toast from 'react-hot-toast'
import { Loader2 } from 'lucide-react'

function Reports() {
  const { companySlug } = useParams()
  const { user, isHRAdmin } = useAuth()
  const [employees, setEmployees] = useState([])
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')
  const [isDownloadingMyZip, setIsDownloadingMyZip] = useState(false)

  useEffect(() => {
    if (!isHRAdmin) return
    const load = async () => {
      try {
        const res = await api.get(`/${companySlug}/admin/employees`)
        setEmployees(res.data)
        if (res.data?.length) {
          setSelectedEmployeeId(String(res.data[0].id))
        }
      } catch (e) {
        toast.error('Failed to load employees')
      }
    }
    load()
  }, [companySlug, isHRAdmin])

  const downloadPersonalInfo = async (employeeId, name) => {
    try {
      const response = await api.get(`/${companySlug}/exports/personal-info/${employeeId}`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Personal_Info_${name}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('PDF downloaded')
    } catch (error) {
      toast.error('Failed to export PDF')
    }
  }

  const downloadAllEmployees = async (type) => {
    try {
      const endpoint = type === 'paysquare'
        ? `/${companySlug}/exports/all-employees/paysquare-template`
        : `/${companySlug}/exports/all-employees/employee-template`
      const response = await api.get(endpoint, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Export downloaded')
    } catch (error) {
      toast.error('Failed to export')
    }
  }

  const downloadMyDocumentsZip = async () => {
    if (isDownloadingMyZip) return
    try {
      setIsDownloadingMyZip(true)
      const response = await api.get(`/${companySlug}/documents/download/all`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const disposition = response.headers['content-disposition'] || ''
      const filenameMatch = disposition.match(/filename="?([^\"]+)"?/) 
      const fallbackName = `employee_${user?.id || 'me'}_documents.zip`
      link.setAttribute('download', filenameMatch?.[1] || fallbackName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Documents ZIP downloaded')
    } catch (error) {
      toast.error('Failed to download documents ZIP')
    } finally {
      setIsDownloadingMyZip(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600 mt-1">
          Download completed onboarding exports and HR templates.
        </p>
      </div>

      <div className="card">
        <h3 className="section-header">Personal Information Form (PDF)</h3>
        <p className="text-sm text-gray-600 mb-4">
          Download the completed form. Includes uploaded passport photo.
        </p>

        {!isHRAdmin && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => downloadPersonalInfo(user?.id, user?.email || 'employee')}
              className="btn-primary"
            >
              Download My Form (PDF)
            </button>
            <button
              onClick={downloadMyDocumentsZip}
              className="btn-secondary disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center"
              disabled={isDownloadingMyZip}
            >
              {isDownloadingMyZip && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isDownloadingMyZip ? 'Preparing ZIP...' : 'Download My Documents (ZIP)'}
            </button>
          </div>
        )}

        {isHRAdmin && (
          <div className="flex items-center space-x-3">
            <select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              className="input max-w-sm"
            >
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.full_name || emp.email}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                const emp = employees.find(e => String(e.id) === String(selectedEmployeeId))
                downloadPersonalInfo(selectedEmployeeId, emp?.full_name || emp?.email || 'employee')
              }}
              className="btn-primary"
              disabled={!selectedEmployeeId}
            >
              Download PDF
            </button>
          </div>
        )}
      </div>

      {isHRAdmin && (
        <div className="card">
          <h3 className="section-header">HR Export Templates (XLSX)</h3>
          <p className="text-sm text-gray-600 mb-4">
            Export all employees in Power Solulever or PaySquare formats.
          </p>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => downloadAllEmployees('employee-template')}
              className="btn-secondary"
            >
              Employee Template
            </button>
            <button
              onClick={() => downloadAllEmployees('paysquare')}
              className="btn-secondary"
            >
              PaySquare Template
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Reports
